import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './QuizGame.css';
import './KBCMultiplex.css';

const KBCMultiplexMode = ({ 
  userRole, 
  userName, 
  userClass, 
  onExit,
  selectedQuestions = null,
  gameConfig = null
}) => {
  const [gameState, setGameState] = useState('setup'); // setup, playing, finished
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [showPrizeLadder, setShowPrizeLadder] = useState(false);
  const [scores, setScores] = useState(0);
  const [lifelines, setLifelines] = useState({
    fiftyFifty: true,
    skip: true,
    hint: true
  });

  const prizeAmounts = [100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000, 500000, 1000000];

  // Audio management system
  const [currentAudio, setCurrentAudio] = useState(null);
  const [timerAudio, setTimerAudio] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);

  // Play audio with proper cleanup
  const playAudio = (audioFile) => {
    try {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
      
      console.log('Playing audio:', audioFile);
      const audio = new Audio(`https://therisingschool.com/Learning%20Games/MP3/${audioFile}`);
      audio.volume = 0.5;
      setCurrentAudio(audio);
      
      audio.play().catch(error => console.log('Audio play failed:', error));
      audio.onended = () => setCurrentAudio(null);
    } catch (error) {
      console.log('Audio not supported:', error);
    }
  };

  // Play timer audio continuously (no countdown, just ambiance)
  const startTimerAudio = () => {
    try {
      if (timerAudio) {
        timerAudio.pause();
        timerAudio.currentTime = 0;
      }
      
      const audio = new Audio('https://therisingschool.com/Learning%20Games/MP3/Timer.mp3');
      audio.volume = 0.3; // Lower volume for background ambiance
      audio.loop = true;
      setTimerAudio(audio);
      
      audio.play().catch(error => console.log('Timer audio play failed:', error));
    } catch (error) {
      console.log('Timer audio not supported:', error);
    }
  };

  // Stop timer audio
  const stopTimerAudio = () => {
    if (timerAudio) {
      timerAudio.pause();
      timerAudio.currentTime = 0;
      setTimerAudio(null);
    }
  };

  // Stop all audio
  const stopAudio = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    stopTimerAudio();
  };

  // Enhanced sound effects
  const playSound = (type) => {
    console.log('playSound called with type:', type);
    switch(type) {
      case 'start':
        if (!gameStarted) {
          console.log('Playing start sound');
          playAudio('Start.mp3');
          setGameStarted(true);
        }
        break;
      case 'question':
        console.log('Playing question sound');
        playAudio('Question.mp3');
        // Start timer audio for ambiance (no countdown)
        setTimeout(() => startTimerAudio(), 1000);
        break;
      case 'lifeline':
        console.log('Playing lifeline sound');
        stopTimerAudio();
        playAudio('Question.mp3');
        break;
      default:
        // Fallback to beep sounds for correct/wrong
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          if (type === 'correct') {
            oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime);
            oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.15);
            oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.3);
            gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.6);
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.6);
          } else if (type === 'wrong') {
            oscillator.frequency.setValueAtTime(330, audioContext.currentTime);
            oscillator.frequency.setValueAtTime(220, audioContext.currentTime + 0.2);
            oscillator.frequency.setValueAtTime(165, audioContext.currentTime + 0.4);
            gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.6);
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.6);
          }
        } catch (error) {
          console.log('Audio not supported');
        }
    }
  };

  // Initialize game with selected questions
  useEffect(() => {
    if (selectedQuestions && selectedQuestions.length >= 12) {
      setQuestions(selectedQuestions.slice(0, 12));
      setGameState('playing');
      // Play start sound when game begins
      setTimeout(() => playSound('start'), 500);
    }
  }, [selectedQuestions]);

  // Play question sound when new question starts
  useEffect(() => {
    if (gameState === 'playing' && questions.length > 0 && !showResult) {
      setTimeout(() => playSound('question'), 100);
    }
  }, [currentQuestion, gameState, showResult]);

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
  };

  const submitAnswer = () => {
    // Stop timer audio when answer is submitted
    stopTimerAudio();
    
    const correct = selectedAnswer === questions[currentQuestion].correctAnswer;
    const points = correct ? prizeAmounts[currentQuestion] : 0;
    
    // Update score
    setScores(scores + points);

    // Play sound effect
    playSound(correct ? 'correct' : 'wrong');

    setShowResult(true);
    setTimeout(() => {
      if (correct && currentQuestion < questions.length - 1) {
        setShowPrizeLadder(true);
      } else if (currentQuestion < questions.length - 1) {
        // Move to next question even if wrong (multiplex mode continues)
        const nextQuestion = currentQuestion + 1;
        setCurrentQuestion(nextQuestion);
        setSelectedAnswer('');
        setShowResult(false);
        setTimeout(() => playSound('question'), 100);
      } else {
        endGame();
      }
    }, 2000);
  };

  const endGame = () => {
    setGameState('finished');
    stopAudio();
  };

  const useFiftyFifty = () => {
    if (!lifelines.fiftyFifty) return;
    
    playSound('lifeline');
    
    const currentQ = questions[currentQuestion];
    const wrongAnswers = currentQ.options.filter(opt => opt !== currentQ.correctAnswer);
    const toRemove = wrongAnswers.slice(0, 2);
    
    setQuestions(prev => {
      const updated = [...prev];
      updated[currentQuestion] = {
        ...updated[currentQuestion],
        options: updated[currentQuestion].options.filter(opt => !toRemove.includes(opt))
      };
      return updated;
    });
    
    setLifelines(prev => ({ ...prev, fiftyFifty: false }));
  };

  const useSkip = () => {
    if (!lifelines.skip || currentQuestion >= questions.length - 1) return;
    
    playSound('lifeline');
    
    setCurrentQuestion(currentQuestion + 1);
    setSelectedAnswer('');
    setLifelines(prev => ({ ...prev, skip: false }));
  };

  const useHint = () => {
    if (!lifelines.hint) return;
    
    playSound('lifeline');
    alert(questions[currentQuestion].hint || "No hint available for this question");
    setLifelines(prev => ({ ...prev, hint: false }));
  };

  if (gameState === 'setup') {
    return (
      <div className="quiz-game">
        <div className="prize-ladder-container">
          <h2>🎮 KBC Multiplex Mode</h2>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div>Setting up your custom game...</div>
            <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
              12 questions • No time limit • Timer sounds for ambiance
            </div>
          </div>
          <button onClick={() => { stopAudio(); onExit(); }} className="quit-btn">Back</button>
        </div>
      </div>
    );
  }

  if (showPrizeLadder) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #6B46C1 0%, #9333EA 50%, #7C3AED 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Spotlight effects */}
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '20%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'pulse 2s infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          top: '60%',
          right: '15%',
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'pulse 3s infinite'
        }}></div>
        
        <div style={{
          background: 'rgba(0,0,0,0.8)',
          padding: '30px',
          borderRadius: '20px',
          border: '3px solid gold',
          boxShadow: '0 0 30px rgba(255,215,0,0.5)',
          maxWidth: '400px',
          width: '90%'
        }}>
          <h2 style={{ color: 'gold', textAlign: 'center', marginBottom: '20px' }}>🏆 Prize Ladder</h2>
          
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {prizeAmounts.map((amount, index) => {
              const isCurrentLevel = index === currentQuestion;
              const isTopPrize = index === prizeAmounts.length - 1;
              const isCompleted = index < currentQuestion;
              
              return (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 15px',
                  margin: '5px 0',
                  borderRadius: '10px',
                  background: isCurrentLevel ? 'linear-gradient(90deg, #10B981, #059669)' :
                             isTopPrize ? 'linear-gradient(90deg, #FFD700, #FFA500)' :
                             isCompleted ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255,255,255,0.1)',
                  border: isCurrentLevel ? '2px solid #10B981' :
                         isTopPrize ? '2px solid gold' : '1px solid rgba(255,255,255,0.2)',
                  color: isTopPrize ? '#000' : '#fff',
                  fontWeight: isCurrentLevel || isTopPrize ? 'bold' : 'normal',
                  fontSize: isTopPrize ? '18px' : '16px',
                  boxShadow: isCurrentLevel ? '0 0 15px rgba(16, 185, 129, 0.5)' :
                            isTopPrize ? '0 0 15px rgba(255, 215, 0, 0.5)' : 'none',
                  transform: isCurrentLevel ? 'scale(1.05)' : 'scale(1)',
                  transition: 'all 0.3s ease'
                }}>
                  <span>{index + 1}.</span>
                  <span>{amount.toLocaleString()} Points</span>
                  {isCurrentLevel && <span>👈 YOU ARE HERE</span>}
                  {isTopPrize && <span>👑</span>}
                  {isCompleted && <span>✅</span>}
                </div>
              );
            })}
          </div>
          
          <button 
            onClick={() => {
              setShowPrizeLadder(false);
              const nextQuestion = currentQuestion + 1;
              setCurrentQuestion(nextQuestion);
              setSelectedAnswer('');
              setShowResult(false);
              setTimeout(() => playSound('question'), 100);
            }}
            style={{
              width: '100%',
              padding: '15px',
              marginTop: '20px',
              background: 'linear-gradient(90deg, #10B981, #059669)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
            }}
          >
            👆 Tap to Continue
          </button>
        </div>
        
        <style>{`
          @keyframes pulse {
            0% { opacity: 0.3; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.1); }
            100% { opacity: 0.3; transform: scale(1); }
          }
        `}</style>
      </div>
    );
  }

  if (gameState === 'finished') {
    return (
      <div className="quiz-game">
        <div className="prize-ladder-container">
          <h2>🏆 Game Complete!</h2>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <h3>Final Score:</h3>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#10b981',
              margin: '10px 0'
            }}>
              {scores.toLocaleString()} Points
            </div>
            <div style={{ fontSize: '16px', color: '#666' }}>
              Questions Completed: {currentQuestion + 1}/12
            </div>
            {gameConfig && (
              <div style={{ fontSize: '14px', color: '#888', marginTop: '10px' }}>
                Subject: {gameConfig.subject} | Class: {gameConfig.class}
              </div>
            )}
          </div>
          <button onClick={() => { stopAudio(); onExit(); }} className="continue-btn">Back to Games</button>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  if (!currentQ) return <div>Loading...</div>;

  return (
    <div className="quiz-game kbc-multiplex-mode">
      <div className="game-header">
        <div className="points-display">
          <div className="coin-animation">
            🪙 {scores.toLocaleString()} Points
          </div>
        </div>
        
        <div className="timer-circle multiplex-timer-circle">
          <div className="timer-inner multiplex-timer-inner">
            <span>∞</span>
          </div>
        </div>
        
        <div className="question-counter">
          {currentQuestion + 1}/12
        </div>
      </div>

      <div className="question-section">
        <div className="question-box multiplex-question-box">
          <div className="multiplex-mode-indicator">
            <span className="multiplex-mode-badge">🎯 Multiplex Mode - No Time Limit</span>
            <span className="multiplex-custom-badge">⚡ CUSTOM GAME</span>
          </div>
          <h3>{currentQ.question}</h3>
        </div>
        
        <div className="options-grid">
          {currentQ.options.map((option, index) => (
            <button
              key={index}
              className={`option-btn ${selectedAnswer === option ? 'selected' : ''} ${
                showResult ? (option === currentQ.correctAnswer ? 'correct' : 
                selectedAnswer === option ? 'wrong' : '') : ''
              }`}
              onClick={() => handleAnswerSelect(option)}
              disabled={showResult}
            >
              <span className="option-letter">{String.fromCharCode(65 + index)}</span>
              <span className="option-text">{option}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="game-controls">
        <div className="lifelines">
          <button 
            className={`lifeline-btn ${!lifelines.fiftyFifty ? 'used' : ''}`}
            onClick={useFiftyFifty}
            disabled={!lifelines.fiftyFifty}
          >
            50:50
          </button>
          <button 
            className={`lifeline-btn ${!lifelines.skip ? 'used' : ''}`}
            onClick={useSkip}
            disabled={!lifelines.skip}
          >
            Skip
          </button>
          <button 
            className={`lifeline-btn ${!lifelines.hint ? 'used' : ''}`}
            onClick={useHint}
            disabled={!lifelines.hint}
          >
            Hint
          </button>
        </div>
        
        <div className="action-buttons">
          <button 
            className="submit-btn"
            onClick={submitAnswer}
            disabled={!selectedAnswer || showResult}
          >
            Final Answer
          </button>
          <button className="quit-btn" onClick={() => { stopAudio(); onExit(); }}>Quit Game</button>
        </div>
      </div>
    </div>
  );
};

export default KBCMultiplexMode;